import React from "react";
import { useGetCmpEmailCaptureAddressStatusQuery } from "../../../graphql/types";
import { memberRequestApolloContext } from "./memberWorkflowShared";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { CmpOrganizationNoun } from "../../../utils/cmpOrganizationNoun";
import { STAFF_SUPPORT_EMAIL } from "../../../utils/userFacingError";

const communicationLogIntro = (entity: "member" | "prospect" = "member") =>
  `Keep track of phone conversation notes, comments, and emails exchanged with this ${entity}.`;
const communicationLogPreviewSentence = <>Email entries also include the <strong>subject line</strong> and can be opened for a full preview.</>;

const communicationLogHistoryNote = (noun: CmpOrganizationNoun) => <span className="block">Each entry shows the <strong>date, staff member, type of communication and details</strong> who recorded it. All communication history is visible only to <strong>authorized {noun.singular} staff</strong>.</span>;

// Update Member Form Communication Log description. The chamber BCC address
// appears inline; loading, failed, missing, and inactive states stay visible.
// The content is phrasing-only because it renders inside the section's <p>.
// Prospects (CMP Dashboard (15) "Prospect Communication Log") pass no
// chamberId: the BCC sentence stays hidden until BCC capture is live (Q15).
export default function MemberEmailCaptureNotice({ chamberId, entity = "member" }: { chamberId?: string; entity?: "member" | "prospect" }) {
  const noun = useCmpOrganizationNoun();
  if (!chamberId) return <span className="flex flex-col gap-[14px] pt-[8px] pb-[14px]">
    <span className="block">{communicationLogIntro(entity)} {communicationLogPreviewSentence}</span>
    {communicationLogHistoryNote(noun)}
  </span>;
  return <CaptureAddressDescription key={chamberId} chamberId={chamberId} />;
}

function CaptureAddressDescription({ chamberId }: { chamberId: string }) {
  const noun = useCmpOrganizationNoun();
  const { data, loading, error, refetch } = useGetCmpEmailCaptureAddressStatusQuery({
    variables: { chamberId }, skip: !chamberId, fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true, context: memberRequestApolloContext,
  });
  const status = !loading && !error ? data?.getCmpEmailCaptureAddressStatus : undefined;
  const address = status?.address;
  const retry = async () => {
    try { await refetch(); }
    catch { /* The query keeps the retry error visible and hides the address. */ }
  };
  return <span className="flex flex-col gap-[14px] pt-[8px] pb-[14px]">
    {address
      ? <span className="block">{communicationLogIntro()} Emails are automatically logged when your {noun.singular}’s designated email address <strong className="break-all">{address}</strong> is added as a <strong>BCC</strong> to an email sent to the member. {communicationLogPreviewSentence}</span>
      : <span className="block">{communicationLogIntro()} {communicationLogPreviewSentence}</span>}
    {loading ? <span role="status" className="block">Loading the {noun.singular} BCC address...</span>
      : error ? <span role="alert" className="block">The {noun.singular} BCC address could not be loaded. <button type="button" className="font-semibold underline" onClick={() => { void retry(); }}>Try Again</button></span>
      : !address ? <span role="status" className="block">Emails are not saved to this log yet. To set up your {noun.singular} BCC address, email {STAFF_SUPPORT_EMAIL}.</span>
      : !status?.captureEnabled ? <span role="status" className="block">Your {noun.singular} BCC address is not turned on yet. To turn it on, email {STAFF_SUPPORT_EMAIL}.</span>
      : null}
    {communicationLogHistoryNote(noun)}
  </span>;
}
